import React, { useEffect } from 'react';
import classNames from 'classnames';
import { withStyles } from 'tss-react/mui';
import {
  ClassNameMap,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  PaperProps,
} from '@mui/material';
import {
  AccountBalance,
  Apartment,
  AttachMoney,
  AllOut,
  Book,
  BugReport,
  Business,
  Chat,
  LockClock,
  Computer,
  Code,
  Email,
  ExpandLess,
  ExpandMore,
  Flag,
  GavelRounded,
  Group,
  GroupWork,
  Key,
  Language,
  ListAlt,
  LocalAtm,
  LocalLibrary,
  LocalOffer,
  LocationCity,
  LocationOn,
  LockOpen,
  MapOutlined,
  MenuBook,
  Note,
  Password,
  People,
  PhotoCamera,
  PlusOne,
  PowerSettingsNew,
  Receipt,
  School,
  Subject,
  VideoCall,
  Work,
} from '@mui/icons-material';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AppNavigationItem } from '../components/navigation';
import { NavigationItems } from '../constants';
import useAuthentication from '../store/useAuthentication';
import useUser from '../store/useUser';

const LOGO_URL = 'https://assets.urbansitter.com/static/img/logos/urbansitter_white_text.svg';

const styles = (theme, _params, classes) => ({
  categoryHeader: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  categoryHeaderPrimary: {
    color: theme.palette.common.white,
  },
  item: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  itemCategory: {
    backgroundColor: theme.palette.common.black,
    boxShadow: '0 -1px 0 #404854 inset',
    paddingTop: 16,
    paddingBottom: 16,
  },
  firebase: {
    fontSize: 24,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.common.white,
  },
  itemActionable: {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.black,
    },
  },
  itemPrimary: {
    color: theme.palette.common.white,
    fontSize: theme.typography.fontSize,
    [`&.${classes.textDense}`]: {
      fontSize: theme.typography.fontSize,
    },
  },
  sectionIcon: {
    color: '#00bbcd',
    minWidth: 40,
  },
  textDense: {},
  divider: {
    marginTop: theme.spacing(1) * 2,
  },
  logout: {
    cursor: 'pointer',
  },
  token: {
    cursor: 'pointer',
    marginTop: '10px',
  },
});

interface Props extends RouteComponentProps {
  classes?: ClassNameMap;
  open?: boolean;
  onClose?: () => void;
  paperProps?: PaperProps;
  variant: 'permanent' | 'persistent' | 'temporary';
}

function Navigator(props: Props) {
  const { classes, location, open = false, onClose, paperProps = {}, variant } = props;

  const [authenticationState, authenticationActions] = useAuthentication();
  const authenticatedUser = authenticationState.authentication.user;
  const isPaymentAdmin = authenticatedUser.roles.filter(role => role.code === 'PAYMENT_ADMIN').length > 0;
  const isSecurityAdmin = authenticatedUser.roles.filter(role => role.code === 'SECURITY_ADMIN').length > 0;
  const isProductAdmin = authenticatedUser.roles.filter(role => role.code === 'PRODUCT_ADMIN').length > 0;
  const isAgencyAdmin = authenticatedUser.roles.filter(role => role.code === 'AGENCY_ADMIN').length > 0;
  const isSuperUser = authenticatedUser.roles.some(role => role.code === 'SUPER_USER');
  const isNonProdEnv = window.location.host.includes('admin-') || window.location.host.includes('localhost');
  const [, userActions] = useUser();

  const [openId, setOpenId] = React.useState('Users');

  const logout = () => {
    authenticationActions.authentication.set({});
  };

  const copyAdminToken = () => {
    const { token } = authenticationState.authentication;

    navigator.clipboard.writeText(`Bearer ${token}`);
  };

  const clearUserState = () => {
    userActions.setUser({});
    userActions.setProfile({});
  };

  type NavChild = {
    id: string;
    icon: JSX.Element;
    url: string;
    onClick?: () => void;
  };

  const userCategories: { id: string; icon: JSX.Element; children: NavChild[] } = {
    id: 'Users',
    icon: <People />,
    children: [
      {
        id: NavigationItems.USER_LOOKUP,
        icon: <People />,
        url: '/users/lookup',
        onClick: clearUserState,
      },
      {
        id: NavigationItems.BATCH_USER_ACTIONS,
        icon: <GroupWork />,
        url: '/users/batch',
      },
      {
        id: NavigationItems.TAGS,
        icon: <LocalOffer />,
        url: '/tags',
      },
      {
        id: NavigationItems.DUPLICATE_SITTERS,
        icon: <People />,
        url: '/duplicate-sitters',
      },
      {
        id: NavigationItems.BATCH_MAILCHIMP,
        icon: <Email />,
        url: '/batch-mailchimp',
      },
    ],
  };

  if (isSecurityAdmin) {
    userCategories.children.push({
      id: NavigationItems.RESET_USER_PASSWORDS,
      icon: <Password />,
      url: '/reset-user-passwords',
    });
  }

  const moderationCategories = {
    id: 'Moderation',
    icon: <GavelRounded />,
    children: [
      {
        id: NavigationItems.RISK_REVIEW,
        icon: <Note />,
        url: '/risk-review',
      },
      {
        id: NavigationItems.BD_FRAUD,
        icon: <Note />,
        url: '/bd-fraud',
      },
      {
        id: NavigationItems.JOB_MODERATION,
        icon: <Note />,
        url: '/job-moderation',
      },
      {
        id: NavigationItems.VIDEO_MODERATION,
        icon: <VideoCall />,
        url: '/video-moderation',
      },
      {
        id: NavigationItems.PHOTO_MODERATION,
        icon: <PhotoCamera />,
        url: '/photo-moderation',
      },
    ],
  };

  const bookingCategories = {
    id: 'Bookings',
    icon: <Book />,
    children: [
      {
        id: NavigationItems.BOOKING_LOOKUP,
        icon: <Book />,
        url: '/bookings/lookup',
      },
    ],
  };

  if (isPaymentAdmin) {
    bookingCategories.children.push({
      id: NavigationItems.BOOKING_BATCH_ACTIONS,
      icon: <AllOut />,
      url: '/bookings/batch',
    });
  }

  const orderCategories = {
    id: 'Orders',
    icon: <AttachMoney />,
    children: [
      {
        id: NavigationItems.ORDER_LOOKUP,
        icon: <AccountBalance />,
        url: '/orders/lookup',
      },
    ],
  };

  if (isPaymentAdmin) {
    orderCategories.children.push({
      id: NavigationItems.ORDER_PAYOUT,
      icon: <LocalAtm />,
      url: '/orders/payout',
    });
    orderCategories.children.push({
      id: NavigationItems.ORDER_PAYOUT_DELAY,
      icon: <LockClock />,
      url: '/orders/payout-delay',
    });
  }

  const marketingCategories = {
    id: 'Marketing',
    icon: <Subject />,
    children: [
      {
        id: NavigationItems.SEO_STATE,
        icon: <Flag />,
        url: '/seo/states',
      },
      {
        id: NavigationItems.SEO_CITY,
        icon: <Apartment />,
        url: '/seo/cities',
      },
      {
        id: NavigationItems.PRESS,
        icon: <MenuBook />,
        url: '/press',
      },
      {
        id: NavigationItems.WINBACK_CODE,
        icon: <Code />,
        url: '/winback-codes',
      },
      {
        id: NavigationItems.MASTER_SERVICE_AREAS,
        icon: <MapOutlined />,
        url: '/master-service-areas',
      },
    ],
  };

  const msaCategories = {
    id: 'Master Service Areas',
    icon: <Language />,
    children: [
      {
        id: NavigationItems.MASTER_SERVICE_AREAS,
        icon: <Language />,
        url: '/master-service-areas',
      },
      {
        id: NavigationItems.MASTER_SERVICE_AREA_TIERS,
        icon: <Apartment />,
        url: '/master-service-area-tiers',
      },
      {
        id: NavigationItems.ZIP_CODE_LOOKUP,
        icon: <LocationOn />,
        url: '/zip-codes',
      },
    ],
  };

  if (isAgencyAdmin) {
    msaCategories.children.push({
      id: NavigationItems.AGENCY_AVAILABILITY,
      icon: <Note />,
      url: '/agency-availability',
    });
    msaCategories.children.push({
      id: NavigationItems.AGENCY_MSA_RATE,
      icon: <Note />,
      url: '/agency-msa-rate',
    });
  }

  const voucherCategories = {
    id: 'Vouchers',
    icon: <Receipt />,
    children: [
      {
        id: NavigationItems.VOUCHER_LOOKUP,
        icon: <ListAlt />,
        url: '/vouchers',
      },
      {
        id: NavigationItems.VOUCHER_ADD,
        icon: <PlusOne />,
        url: '/voucher-create',
      },
      {
        id: NavigationItems.PARTNERS,
        icon: <ListAlt />,
        url: '/partners',
      },
    ],
  };

  const groupCategories = {
    id: 'Groups',
    icon: <GroupWork />,
    children: [
      {
        id: NavigationItems.GROUPS_AFFILIATIONS,
        icon: <Group />,
        url: '/affiliations',
      },
      {
        id: NavigationItems.GROUPS_SCHOOLS,
        icon: <LocalLibrary />,
        url: '/schools',
      },
      {
        id: NavigationItems.GROUPS_LOCAL_GROUPS,
        icon: <LocationCity />,
        url: '/local-groups',
      },
      {
        id: NavigationItems.GROUPS_COLLEGES,
        icon: <School />,
        url: '/colleges',
      },
    ],
  };

  const qaCategories = {
    id: 'QA Tools',
    icon: <BugReport />,
    children: [
      {
        id: NavigationItems.QA_ACTIVITY_GENERATOR,
        icon: <GroupWork />,
        url: '/activity-generator',
      },
      {
        id: NavigationItems.QA_API_PROFILER,
        icon: <Computer />,
        url: '/api-profiler',
      },
      {
        id: NavigationItems.QA_SEED_TEST_USER,
        icon: <People />,
        url: '/seed-test-user',
      },
      {
        id: NavigationItems.QA_SSO_TEST,
        icon: <LockOpen />,
        url: '/sso-test',
      },
      {
        id: NavigationItems.QA_MEMBERSHIP_RENEWAL,
        icon: <AttachMoney />,
        url: '/membership-renewal',
      },
    ],
  };

  const bizDevCategories = {
    id: 'Business Dev',
    icon: <Business />,
    children: [
      {
        id: NavigationItems.COMPANIES,
        icon: <Work />,
        url: '/companies',
      },
      {
        id: NavigationItems.SMALL_BUSINESS,
        icon: <Work />,
        url: '/small-business',
      },
      {
        id: NavigationItems.PRICING_PLANS,
        icon: <Work />,
        url: '/pricing-plans',
      },
      {
        id: NavigationItems.EMPLOYEES,
        icon: <People />,
        url: '/company-employees',
      },
    ],
  };

  const productCategories = {
    id: 'Product',
    icon: <ListAlt />,
    children: [
      {
        id: NavigationItems.AB_TEST_ASSIGNMENT,
        icon: <Computer />,
        url: '/ab-test-assignment',
      },
      {
        id: NavigationItems.SPECIAL_EXPERIENCES,
        icon: <Note />,
        url: '/special-experiences',
      },
      {
        id: NavigationItems.SPECIAL_SERVICES,
        icon: <Note />,
        url: '/special-services',
      },
      {
        id: NavigationItems.SPECIAL_SITUATIONS,
        icon: <Note />,
        url: '/special-situations',
      },
      {
        id: NavigationItems.INCLUDES,
        icon: <Note />,
        url: '/includes',
      },
      {
        id: NavigationItems.SWEETENERS,
        icon: <Note />,
        url: '/sweeteners',
      },
      {
        id: NavigationItems.TXN_EMAIL_TEMPLATES,
        icon: <Email />,
        url: '/txn-email-templates',
      },
      {
        id: NavigationItems.KNOWLEDGE_DOCUMENTS,
        icon: <MenuBook />,
        url: '/knowledge-documents',
      },
      {
        id: NavigationItems.CHAT_HISTORY,
        icon: <Chat />,
        url: '/chat-history',
      },
    ],
  };

  const nav = [
    userCategories,
    moderationCategories,
    bookingCategories,
    orderCategories,
    marketingCategories,
    msaCategories,
    voucherCategories,
    groupCategories,
    ...(isSuperUser && isNonProdEnv ? [qaCategories] : []),
    bizDevCategories,
    ...(isProductAdmin ? [productCategories] : []),
  ];

  const isOpen = id => id === openId;

  const sectionClick = id => {
    if (openId === id) {
      setOpenId('');
    } else {
      setOpenId(id);
    }
  };

  useEffect(() => {
    nav.forEach(item => {
      item.children.forEach(child => {
        if (child.url === location.pathname) {
          setOpenId(item.id);
        }
      });
    });
  }, [location.pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      PaperProps={paperProps}
      variant={variant}
    >
      <List disablePadding>
        <ListItem
          className={classNames(
            classes.firebase,
            classes.item,
            classes.itemCategory,
          )}
        >
          <img src={LOGO_URL} alt='UrbanSitter' style={{ height: 32 }} />
        </ListItem>
        {nav.map(({ id, icon, children }) => (
          <React.Fragment key={id}>
            <ListItem
              button
              onClick={() => sectionClick(id)}
              className={classNames(
                classes.firebase,
                classes.item,
                classes.itemCategory,
              )}
            >
              <ListItemIcon className={classes.sectionIcon}>{icon}</ListItemIcon>
              <ListItemText
                primary={id}
                classes={{
                  primary: classes.categoryHeaderPrimary,
                }}
              />
              {isOpen(id) ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={isOpen(id)} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                {(children as NavChild[]).map(({ id: text, icon: childIcon, url, onClick }) => (
                  <AppNavigationItem
                    key={text}
                    text={text}
                    icon={childIcon}
                    url={url}
                    active={location.pathname === url}
                    onClick={onClick}
                  />
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
        <Divider className={classes.divider} />
        <ListItem
          button
          dense
          className={classNames(
            classes.item,
            classes.itemActionable,
            classes.token,
          )}
          onClick={copyAdminToken}
        >
          <ListItemIcon>
            <Key />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.itemPrimary,
            }}
          >
            Copy Admin Token
          </ListItemText>
        </ListItem>
        <ListItem
          button
          dense
          className={classNames(
            classes.item,
            classes.itemActionable,
            classes.logout,
          )}
          onClick={logout}
        >
          <ListItemIcon>
            <PowerSettingsNew />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.itemPrimary,
            }}
          >
            Logout
          </ListItemText>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default withRouter(withStyles(Navigator, styles));
